import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Heart, Activity } from 'lucide-react';
import { useResults } from '../lib/useResults';
import { SaveResultButton } from '../components/tools/SaveResultButton';
import { CalculatorJsonLd } from '../components/seo/SchemaMarkup';
import { ToolSEOWrapper } from '../components/tools/ToolSEOWrapper';

const HR_SEO = {
  name: "Heart Rate Training Zones Calculator",
  description: "Calculate your 5 heart rate training zones using the Karvonen or standard method. Optimize your cardio for fat burning, endurance, and VO2 max.",
  url: "https://ninjathlete.com/tools/heart-rate-zones",
  steps: [
    { name: "Enter your age", text: "Input your age to calculate your maximum heart rate (220 - age)." },
    { name: "Enter resting heart rate (optional)", text: "For more accurate zones using the Karvonen method, provide your resting heart rate measured first thing in the morning." },
    { name: "Select calculation method", text: "Choose Karvonen (heart rate reserve, more accurate) or Standard (percentage of max HR)." },
    { name: "Calculate zones", text: "Click Calculate to see all 5 training zones with BPM ranges, benefits, and perceived effort levels." },
  ],
  faqs: [
    { question: "What are heart rate training zones?", answer: "Heart rate zones are ranges of heartbeats per minute that correspond to different exercise intensities. Zone 1 is recovery, Zone 2 is aerobic base, Zone 3 is tempo, Zone 4 is threshold, and Zone 5 is max effort." },
    { question: "What is the best zone for fat burning?", answer: "Zone 2 (60-70% max HR) is optimal for fat oxidation. However, higher zones burn more total calories. A mix of Zone 2 base training with Zone 4-5 intervals is most effective for body composition." },
    { question: "How do I find my resting heart rate?", answer: "Measure your pulse first thing in the morning before getting out of bed. Count beats for 60 seconds, or for 15 seconds and multiply by 4. Do this for 3 days and average the results." },
  ],
};

const ZONE_COLORS = ['#22c55e', '#00F0FF', '#FFD100', '#ff6b35', '#E63946'];

const ZONES = [
  { zone: 1, name: 'Recovery / Warm-Up', pctLow: 50, pctHigh: 60, benefit: 'Active recovery, fat burning, base endurance', feel: 'Very light — can hold a conversation easily' },
  { zone: 2, name: 'Aerobic / Base', pctLow: 60, pctHigh: 70, benefit: 'Builds aerobic capacity, improves fat metabolism', feel: 'Comfortable — can talk in full sentences' },
  { zone: 3, name: 'Tempo / Threshold', pctLow: 70, pctHigh: 80, benefit: 'Improves efficiency, increases lactate threshold', feel: 'Moderate effort — can speak in short phrases' },
  { zone: 4, name: 'Lactate Threshold', pctLow: 80, pctHigh: 90, benefit: 'Increases speed endurance, VO2 max improvement', feel: 'Hard — difficult to talk, heavy breathing' },
  { zone: 5, name: 'Max Effort / VO2 Max', pctLow: 90, pctHigh: 100, benefit: 'Maximum power output, anaerobic capacity', feel: 'All-out — cannot speak, unsustainable beyond 1-2 min' },
];

export default function HeartRateZonesPage() {
  const [age, setAge] = useState('');
  const [restingHR, setRestingHR] = useState('');
  const [method, setMethod] = useState('karvonen');
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(false);
  const { saveResult } = useResults();

  const calculate = () => {
    if (!age) return;
    const maxHR = 220 - parseInt(age);
    const rhr = restingHR ? parseInt(restingHR) : 60;

    const zones = ZONES.map((z, i) => {
      let low, high;
      if (method === 'karvonen') {
        // Karvonen: Target HR = ((MHR - RHR) * %intensity) + RHR
        const hrr = maxHR - rhr;
        low = Math.round(hrr * (z.pctLow / 100) + rhr);
        high = Math.round(hrr * (z.pctHigh / 100) + rhr);
      } else {
        // Simple percentage of max HR
        low = Math.round(maxHR * z.pctLow / 100);
        high = Math.round(maxHR * z.pctHigh / 100);
      }
      return { ...z, low, high, color: ZONE_COLORS[i] };
    });

    setResult({ maxHR, restingHR: rhr, zones });
    setSaved(false);
  };

  return (
    <ToolSEOWrapper toolKey="heart-rate-zones">
    <main className="pt-20 min-h-screen" data-testid="heart-rate-zones-page">
      <CalculatorJsonLd {...HR_SEO} calculatorName={HR_SEO.name} />
      <section className="container-ninja section-spacing">
        <Link to="/tools" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /><span className="text-sm uppercase tracking-wider font-heading">Back to Tools</span>
        </Link>
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 text-xs uppercase tracking-wider bg-[#E63946] text-white mb-4">Calculator</span>
            <h1 className="text-4xl md:text-5xl text-white font-heading mb-3" data-testid="hr-calc-title">Heart Rate Zone Calculator</h1>
            <p className="text-zinc-400">Find your personalized training zones for optimal performance</p>
          </div>

          <div className="bg-zinc-900/80 border border-zinc-800 p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div data-testid="hr-age-input">
                <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-2 font-heading">Age</label>
                <input type="number" value={age} onChange={e => setAge(e.target.value)} placeholder="Your age"
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:border-[#E63946] focus:outline-none transition-colors" />
              </div>
              <div data-testid="hr-resting-input">
                <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-2 font-heading">Resting HR (optional)</label>
                <input type="number" value={restingHR} onChange={e => setRestingHR(e.target.value)} placeholder="e.g., 60 bpm"
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:border-[#E63946] focus:outline-none transition-colors" />
              </div>
              <div>
                <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-2 font-heading">Method</label>
                <div className="flex gap-2">
                  {[{ v: 'karvonen', l: 'Karvonen' }, { v: 'simple', l: '%Max HR' }].map(m => (
                    <button key={m.v} onClick={() => setMethod(m.v)} className={`flex-1 py-3 text-xs font-heading uppercase transition-colors ${method === m.v ? 'bg-[#E63946] text-white' : 'bg-zinc-800 text-zinc-400 border border-zinc-700'}`}>{m.l}</button>
                  ))}
                </div>
              </div>
            </div>
            <button onClick={calculate} disabled={!age}
              className="w-full py-4 bg-gradient-to-r from-[#E63946] to-[#ff6b35] text-white font-heading text-lg uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              data-testid="calculate-hr-btn">
              Calculate My Zones
            </button>
          </div>

          {result && (
            <div className="mt-8 space-y-6 animate-entrance" data-testid="hr-results">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-zinc-900/80 border border-zinc-800 p-6 text-center">
                  <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Max Heart Rate</p>
                  <p className="text-4xl font-heading text-white">{result.maxHR} <span className="text-lg text-[#E63946]">bpm</span></p>
                </div>
                <div className="bg-zinc-900/80 border border-zinc-800 p-6 text-center">
                  <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Resting Heart Rate</p>
                  <p className="text-4xl font-heading text-zinc-300">{result.restingHR} <span className="text-lg text-zinc-500">bpm</span></p>
                </div>
              </div>

              {/* Zone Cards */}
              <div className="space-y-3">
                {result.zones.map((z) => (
                  <div key={z.zone} className="bg-zinc-900/80 border border-zinc-800 p-5 hover:border-zinc-700 transition-colors" data-testid={`hr-zone-${z.zone}`}>
                    <div className="flex items-center gap-4 mb-3">
                      <div className="w-10 h-10 flex items-center justify-center font-heading text-lg text-black flex-shrink-0" style={{ backgroundColor: z.color }}>
                        {z.zone}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="text-white font-semibold text-sm">{z.name}</h4>
                          <span className="font-heading text-lg" style={{ color: z.color }}>{z.low}-{z.high} bpm</span>
                        </div>
                        <p className="text-zinc-600 text-xs">{z.pctLow}-{z.pctHigh}% intensity</p>
                      </div>
                    </div>
                    {/* Zone bar */}
                    <div className="h-2 bg-zinc-800 rounded-full overflow-hidden mb-2">
                      <div className="h-full rounded-full" style={{ width: `${z.pctHigh}%`, backgroundColor: z.color, opacity: 0.7 }} />
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-2">
                      <div><p className="text-zinc-500 text-[10px] uppercase">Benefit</p><p className="text-zinc-400 text-xs">{z.benefit}</p></div>
                      <div><p className="text-zinc-500 text-[10px] uppercase">How it Feels</p><p className="text-zinc-400 text-xs">{z.feel}</p></div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-zinc-900/80 border border-zinc-800 p-6">
                <h3 className="text-white text-lg font-heading mb-3 flex items-center gap-2"><Activity className="w-5 h-5 text-[#00F0FF]" /> HYROX Training Tip</h3>
                <p className="text-zinc-400 text-sm">For HYROX races, train primarily in Zones 2-3 for base endurance, with threshold intervals in Zone 4. Station work (sled push, wall balls) will push you into Zone 4-5.</p>
                <Link to="/blogs/article/the-ultimate-guide-to-hyrox-training-plans-and-workouts-get-race-ready" className="text-[#00F0FF] text-sm hover:text-white transition-colors mt-3 inline-block">
                  Read: Ultimate HYROX Training Guide &#8594;
                </Link>
              </div>
              <SaveResultButton
                saved={saved}
                onSave={() => {
                  saveResult('heartRate', {
                    value: result.maxHR,
                    label: `Max HR: ${result.maxHR} bpm | Zone 2: ${result.zones[1].low}-${result.zones[1].high}`,
                    maxHR: result.maxHR, restingHR: result.restingHR,
                    zones: result.zones.map(z => ({ zone: z.zone, low: z.low, high: z.high, name: z.name })),
                  });
                  setSaved(true);
                }}
              />
            </div>
          )}
        </div>
      </section>
    </main>
    </ToolSEOWrapper>
  );
}
